/**
 * Segment Customer List Component
 *
 * Displays a sortable, paginated table of customers in an RFM segment with:
 * - Customer details (ID, name, segment)
 * - RFM scores (R, F, M, composite)
 * - Customer metrics (revenue, purchases, last purchase date)
 * - Sorting capabilities
 * - Pagination controls
 */

import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/frontend/components/ui/table";
import { Button } from "@/frontend/components/ui/button";
import { Badge } from "@/frontend/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import type { CustomerRFMData } from "@/shared/types/rfm-analytics";
import { CUSTOMER_LIST_TRANSLATIONS, RFM_LABELS, formatThaiDate } from "@/shared/lib/rfm-translations";

interface SegmentCustomerListProps {
  customers: CustomerRFMData[];
  totalCustomers: number;
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

type SortField = 'name' | 'recency' | 'frequency' | 'monetary' | 'composite' | 'revenue' | 'purchases' | 'lastPurchase';
type SortDirection = 'asc' | 'desc';

export function SegmentCustomerList({
  customers,
  totalCustomers,
  currentPage,
  pageSize,
  hasMore,
  onPageChange,
  isLoading = false
}: SegmentCustomerListProps) {

  const [sortField, setSortField] = useState<SortField>('composite');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date using centralized helper (YYYY-MM-DD format)
  const formatDate = formatThaiDate;

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort customers
  const sortedCustomers = [...customers].sort((a, b) => {
    let aValue: any, bValue: any;

    switch (sortField) {
      case 'name':
        aValue = a.customerName.toLowerCase();
        bValue = b.customerName.toLowerCase();
        break;
      case 'recency':
        aValue = a.rfmScore.recencyScore;
        bValue = b.rfmScore.recencyScore;
        break;
      case 'frequency':
        aValue = a.rfmScore.frequencyScore;
        bValue = b.rfmScore.frequencyScore;
        break;
      case 'monetary':
        aValue = a.rfmScore.monetaryScore;
        bValue = b.rfmScore.monetaryScore;
        break;
      case 'composite':
        aValue = a.rfmScore.compositeScore;
        bValue = b.rfmScore.compositeScore;
        break;
      case 'revenue':
        aValue = a.metrics.totalRevenue;
        bValue = b.metrics.totalRevenue;
        break;
      case 'purchases':
        aValue = a.metrics.totalPurchases;
        bValue = b.metrics.totalPurchases;
        break;
      case 'lastPurchase':
        aValue = new Date(a.metrics.lastPurchaseDate).getTime();
        bValue = new Date(b.metrics.lastPurchaseDate).getTime();
        break;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination info
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCustomers);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{CUSTOMER_LIST_TRANSLATIONS.title}</CardTitle>
          <Badge variant="outline">
            {totalCustomers.toLocaleString()} {CUSTOMER_LIST_TRANSLATIONS.customers}
          </Badge>
        </div>
      </CardHeader>

      <CardContent>
        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('name')}
                  >
                    {CUSTOMER_LIST_TRANSLATIONS.customer}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('recency')}
                  >
                    {RFM_LABELS.r}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('frequency')}
                  >
                    {RFM_LABELS.f}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('monetary')}
                  >
                    {RFM_LABELS.m}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('composite')}
                  >
                    {CUSTOMER_LIST_TRANSLATIONS.score}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('revenue')}
                  >
                    {CUSTOMER_LIST_TRANSLATIONS.revenue}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('purchases')}
                  >
                    {CUSTOMER_LIST_TRANSLATIONS.purchases}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-semibold"
                    onClick={() => handleSort('lastPurchase')}
                  >
                    {CUSTOMER_LIST_TRANSLATIONS.lastPurchase}
                    <ArrowUpDown className="ml-2 h-3 w-3" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : sortedCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center text-gray-500">
                    {CUSTOMER_LIST_TRANSLATIONS.noCustomers}
                  </TableCell>
                </TableRow>
              ) : (
                sortedCustomers.map((customer) => (
                  <TableRow key={customer.customerId}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{customer.customerName}</div>
                        <div className="text-xs text-gray-500">{customer.customerId}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="font-mono">
                        {customer.rfmScore.recencyScore}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="font-mono">
                        {customer.rfmScore.frequencyScore}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="font-mono">
                        {customer.rfmScore.monetaryScore}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="font-mono font-semibold">
                        {customer.rfmScore.compositeScore}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(customer.metrics.totalRevenue)}
                    </TableCell>
                    <TableCell className="text-center">
                      {customer.metrics.totalPurchases}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-sm">{formatDate(customer.metrics.lastPurchaseDate)}</div>
                        <div className="text-xs text-gray-500">
                          {customer.metrics.daysSinceLastPurchase} {CUSTOMER_LIST_TRANSLATIONS.daysAgo}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!isLoading && sortedCustomers.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              {CUSTOMER_LIST_TRANSLATIONS.showing} {startIndex} {CUSTOMER_LIST_TRANSLATIONS.to} {endIndex} {CUSTOMER_LIST_TRANSLATIONS.of} {totalCustomers} {CUSTOMER_LIST_TRANSLATIONS.customers}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {CUSTOMER_LIST_TRANSLATIONS.previous}
              </Button>

              <div className="text-sm font-medium px-3">
                {CUSTOMER_LIST_TRANSLATIONS.page} {currentPage}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={!hasMore}
              >
                {CUSTOMER_LIST_TRANSLATIONS.next}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
